import React, { useState } from 'react';
import { Calendar, Download, Users, UserCheck, LogIn, Building2 } from 'lucide-react';
import KPICard from '../components/KPICard';
import DataTable from '../components/DataTable';
import QueryInfoButton from '../components/QueryInfoButton';
import { useTenantAdoptionReport, useTenantAdoptionSummary } from '../hooks/useReports';
import { formatNumber, formatDate } from '../utils/formatters';
import { TENANT_ADOPTION_QUERIES } from '../constants/queries';

export default function TenantAdoption() {
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  const { data: reportData, isLoading: reportLoading } = useTenantAdoptionReport(
    dateRange.start,
    dateRange.end
  );
  const { data: summaryData, isLoading: summaryLoading } = useTenantAdoptionSummary();

  const summary = summaryData?.data?.[0] || {};

  const columns = [
    { key: 'date_day', label: 'Date', sortable: true },
    { key: 'tenant_name', label: 'Tenant', sortable: true },
    { key: 'new_adopted_consumers', label: 'New Onboarded', sortable: true, format: 'number' },
    { key: 'count_logins', label: 'Logins', sortable: true, format: 'number' },
    { key: 'total_adopted_consumers', label: 'Total Onboarded', sortable: true, format: 'number' },
    { key: 'total_members', label: 'Total Members', sortable: true, format: 'number' },
    { key: 'percentage_onboarded', label: '% Onboarded', sortable: true, format: 'percent' },
  ];

  const formattedData = reportData?.data?.map(row => ({
    ...row,
    date_day: formatDate(row.date_day),
  })) || [];

  const handleExport = () => {
    if (!reportData?.data) return;

    const headers = columns.map((col) => col.label).join(',');
    const rows = reportData.data.map((row) =>
      columns
        .map((col) => {
          const value = row[col.key];
          if (col.key === 'date_day') return formatDate(value);
          if (col.format === 'number') return formatNumber(value);
          if (col.format === 'percent') return `${value}%`;
          return `"${value}"`;
        })
        .join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tenant-adoption-${dateRange.start}-to-${dateRange.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = [
    {
      title: 'Total Members Onboarded',
      value: summary.total_members_onboarded,
      icon: UserCheck,
      format: 'compact',
    },
    {
      title: 'Total Members Logged In',
      value: summary.total_members_logged_in,
      icon: LogIn,
      format: 'compact',
    },
    {
      title: 'Active Tenants',
      value: summary.total_tenants,
      icon: Building2,
      format: 'number',
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Tenant Adoption Report</h2>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            Daily logins and member onboarding metrics per tenant
          </p>
        </div>
        <QueryInfoButton
          title="Tenant Adoption Data Sources"
          queries={[
            TENANT_ADOPTION_QUERIES.report,
            TENANT_ADOPTION_QUERIES.summary,
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {kpis.map((kpi, index) => (
          <KPICard key={index} {...kpi} loading={summaryLoading} />
        ))}
      </div>

      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Daily Adoption Data</h3>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="input w-40"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="input w-40"
              />
            </div>

            <button onClick={handleExport} className="btn-secondary">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        <DataTable
          data={formattedData}
          columns={columns}
          loading={reportLoading}
          emptyMessage="No adoption data available for the selected period"
        />
      </div>

      <div className="card bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">About This Report</h4>
            <p className="text-sm text-amber-700 mt-1">
              This report shows daily member adoption and login activity per tenant.
              <strong> New Onboarded</strong> represents members who completed onboarding on that day.
              <strong> Logins</strong> shows total login events.
              <strong> Total Onboarded</strong> is the cumulative count up to that date.
              <strong> Total Members</strong> shows the total number of active members in each tenant.
              <strong> % Onboarded</strong> shows the percentage of total members who have been onboarded.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
